import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { microsoftRequest } from "@shared/services/EnvironmentVariables";
import { useMsal } from "@azure/msal-react";
import { AuthenticationResult } from "@azure/msal-browser";
import { failureNotification } from "@shared/store/notification.duck";
import { TrackJS } from "trackjs";
import styled from "styled-components";
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { DropDownListItem } from '@shared/ui/organisms/drop-down-list/ReactSelectDropDownList';
import { REACT_SELECT_DROPDOWN_STYLES } from '@shared/ui/organisms/OfficeDropDownList';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import { useTranslation } from "react-i18next";
import TextField from '@shared/ui/atoms/TextField';
import { AuthType } from "@shared/utils/AuthUtils";
import CreatableSelect from "react-select/creatable";

const REQUIRED_SCOPES = ["User.Read", "User.Read.All", "User.ReadBasic.All"];

export function OutlookMeetingRoomLookup(props: Props) {
  const {loading, onChange, microsoftRoomId} = props;
  const dispatch = useDispatch();
  const attemptedToLoadMeetingRoomsList = useRef(false);
  const user = useSelector(selectCurrentUser);
  const {t} = useTranslation();
  const {instance} = useMsal();

  const [isComponentLoading, setIsComponentLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [selectedItem, setSelectedItem] = useState<DropDownListItem | undefined>(undefined);
  const [meetingRoomDropDownOptions, setMeetingRoomDropDownOptions] = useState<DropDownListItem[]>([]);

  const isMicrosoftAuth = user?.authType === AuthType.Microsoft
  const showFreeTextInput = (!isMicrosoftAuth || !accessToken);

  const loadMeetingRoomsList = useCallback(async (token?: string) => {
    if (attemptedToLoadMeetingRoomsList.current) {
      return;
    }

    const tokenWithFallback = token ?? accessToken;
    setIsComponentLoading(true);
    attemptedToLoadMeetingRoomsList.current = true;
    try {
      const response = await fetch(`https://graph.microsoft.com/beta/me/findRooms`, {
        method: 'GET',
        headers: {
          'Authorization': 'Bearer ' + tokenWithFallback
        }
      });
      const data = await response.json();
      setMeetingRoomDropDownOptions(data.value.map((room: any) => ({value: room.address, label: room.address})));
    } catch (e: any) {
      dispatch(failureNotification('Failed to load meeting room data from Microsoft'))
      TrackJS?.track(e)
    } finally {
      setIsComponentLoading(false);
    }
  }, [accessToken, dispatch])

  useEffect(() => {
    if (isMicrosoftAuth) {
      setIsComponentLoading(true);
      instance.acquireTokenSilent({ ...microsoftRequest.login, scopes: REQUIRED_SCOPES, })
        .then((result: AuthenticationResult) => {
          setAccessToken(result.accessToken)
          return result.accessToken;
        })
        .then((accessToken) => loadMeetingRoomsList(accessToken))
        .catch((err) => {
          dispatch(failureNotification('Reload permissions'));
          console.error(err)
        })
        .finally(() => setIsComponentLoading(false));
    }
  }, [dispatch, instance, isMicrosoftAuth, loadMeetingRoomsList]);

  useEffect(() => {
    const currentSelectedItem = meetingRoomDropDownOptions.find(mr => mr.value === microsoftRoomId)
    if (!currentSelectedItem && microsoftRoomId) {
      const newItem = {
        value: microsoftRoomId,
        label: microsoftRoomId,
      }
      setSelectedItem(newItem);
    } else {
      setSelectedItem(currentSelectedItem);
    }
  }, [meetingRoomDropDownOptions, microsoftRoomId]);

  const fetchPermissions = async () => {
    if (isMicrosoftAuth) {
      attemptedToLoadMeetingRoomsList.current = false;
      setIsComponentLoading(true);
      setAccessToken('');
      try {
        const result = await instance.acquireTokenPopup({
          scopes: REQUIRED_SCOPES,
          redirectUri: `${window.location.origin}/blank.html`,
        })
        setAccessToken(result.accessToken);
        await loadMeetingRoomsList(result.accessToken);
      } finally {
        setIsComponentLoading(false);
      }
    }
  }

  const onDropDownChange = (item: any) => {
    if (item) {
      onChange(item.value);
    }
  }

  return <OutlookMeetingRoomContainer>
    <BodySmall className="textField__label" weight={600}>{t('settings.meeting-rooms.meeting-room-email-address')}</BodySmall>
    <Wrapper>
      {showFreeTextInput ? <>
        <TextField disabled={loading}
                   value={microsoftRoomId}
                   onChange={props.onChange}
                   style={{marginTop: 24, marginBottom: 12}}
                   fullWidth={true} />
      </> : <>
        <DropDownWrapper>
          <CreatableSelect
            options={meetingRoomDropDownOptions}
            onChange={onDropDownChange}
            value={selectedItem}
            styles={REACT_SELECT_DROPDOWN_STYLES}
            closeMenuOnSelect={true}
            isMulti={false}
            isLoading={loading || isComponentLoading}
          />
          {((!accessToken && isMicrosoftAuth) || (meetingRoomDropDownOptions.length === 0 && isMicrosoftAuth)) && <IconButton icon={IconTypes.Redo}
                                                                                                                              disabled={loading || isComponentLoading}
                                                                                                                              onClick={fetchPermissions} />}
        </DropDownWrapper>
      </>}
    </Wrapper>
    <BodySmall weight={600}>{t('settings.meeting-rooms.important')}</BodySmall>
    <BodySmall>{t('settings.meeting-rooms.important-message')}</BodySmall>
  </OutlookMeetingRoomContainer>
}

interface Props {
  loading: boolean;
  microsoftRoomId: string;
  onChange: (roomName: string) => void;
}


const DropDownWrapper = styled.div`
  flex: 1;
  display: flex;
  > div:first-child {
    max-width: 400px;
  }
`

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  .textField {
    margin: 0 !important;
    max-width: 400px;
  }
`

const OutlookMeetingRoomContainer = styled.div`
  border-radius: 12px;
  background-color: #E6FCFF;
  padding: 8px;
`
